import { ConflictException, Injectable, NotFoundException, Optional } from '@nestjs/common';
import { AddWishlistItemRequest, WishlistItemSummary, WishlistResponse } from '@cart/shared-types';
import { AuditService } from '../audit/audit.service';
import { WishlistRepository } from './wishlist.repository';

@Injectable()
export class WishlistService {
  constructor(
    private readonly wishlistRepository: WishlistRepository,
    @Optional()
    private readonly auditService?: AuditService
  ) {}

  async list(userId: string): Promise<WishlistResponse> {
    const items = await this.wishlistRepository.list(userId);

    return {
      items,
      total: items.length,
    };
  }

  async add(userId: string, input: AddWishlistItemRequest): Promise<WishlistItemSummary> {
    const product = await this.wishlistRepository.findActiveProduct(input.productId);

    if (!product) {
      throw new NotFoundException('Product not found');
    }

    if (await this.wishlistRepository.exists(userId, product.id)) {
      throw new ConflictException('Product already exists in wishlist');
    }

    const item = await this.wishlistRepository.add(userId, product);

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'wishlist_items',
      recordId: item.id,
      operation: 'create',
      changedColumns: ['userId', 'productId'],
      newValue: item,
      changedBy: userId,
      source: 'WishlistService.add',
    });

    return item;
  }

  async delete(userId: string, itemId: string): Promise<void> {
    const deleted = await this.wishlistRepository.delete(userId, itemId);

    if (!deleted) {
      throw new NotFoundException('Wishlist item not found');
    }

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'wishlist_items',
      recordId: itemId,
      operation: 'delete',
      changedBy: userId,
      source: 'WishlistService.delete',
    });
  }
}
